// src/components/EquipmentManager/MyAssignmentsView.jsx

import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaCheckCircle, FaExclamationTriangle, FaClock, FaStar, FaEdit, FaTrash, FaSearch } from 'react-icons/fa';
import { auth } from '../../firebaseConfig';
import { useNotification } from '../../context/NotificationContext';
import ConfirmationModal from '../ConfirmationModal/ConfirmationModal';
import AssignEquipmentModal from './AssignEquipmentModal';
import WriteReviewModal from './WriteReviewModal';
import styles from './MyAssignmentsView.module.css';

const MyAssignmentsView = ({ programId, canManage = false, userId }) => {
  const { showSuccess, showError } = useNotification();
  const [assignments, setAssignments] = useState({ active: [], overdue: [], history: [] });
  const [allAssignments, setAllAssignments] = useState({ active: [], overdue: [], history: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [equipmentToReview, setEquipmentToReview] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [assignmentToEdit, setAssignmentToEdit] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // all, active, overdue, history
  const [playerFilter, setPlayerFilter] = useState(''); // filter by player
  const [availablePlayers, setAvailablePlayers] = useState([]);
  
  useEffect(() => {
    fetchMyAssignments();
  }, [programId]);
  
  const fetchMyAssignments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get Firebase auth token
      const currentUser = auth.currentUser;
      const token = currentUser ? await currentUser.getIdToken() : null;
      const currentUserId = userId || currentUser?.uid;
      
      // Managers: Get ALL program assignments
      // Members: Get only their own assignments
      const endpoint = canManage 
        ? `/api/equipment-assignments/program/${programId}`
        : `/api/equipment-assignments/my-assignments`;
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}${endpoint}`,
        {
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Assignment fetch failed:', response.status, errorText);
        throw new Error(`Failed to fetch assignments: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (canManage) {
        // For managers: Process raw assignment array from program endpoint
        let assignmentsArray = Array.isArray(data) ? data : (data.assignments || []);
        processAssignments(assignmentsArray);
      } else {
        // For members: Use structured data from my-assignments endpoint
        setAssignments(data);
      }
      
    } catch (err) {
      console.error('Error fetching assignments:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  const processAssignments = (assignmentsArray) => {
    const now = new Date();
    
    const active = [];
    const overdue = [];
    const history = [];
    
    assignmentsArray.forEach(assignment => {
      const status = (assignment.status || '').toLowerCase();
      
      if (status === 'returned') {
        history.push(assignment);
      } else if (status === 'active' || status === 'assigned') {
        // Check if overdue
        if (assignment.dueDate && new Date(assignment.dueDate) < now) {
          overdue.push(assignment);
        } else {
          active.push(assignment);
        }
      }
    });
    
    const stats = {
      currentlyActive: active.length,
      overdue: overdue.length,
      returned: history.length,
      reviewedCount: history.filter(a => a.hasBeenReviewed).length,
      totalAssigned: assignmentsArray.length
    };
    
    const categorizedAssignments = { active, overdue, history, stats };
    setAllAssignments(categorizedAssignments);
    setAssignments(categorizedAssignments); // Initial set
    
    // Extract unique players for filter dropdown (managers only)
    if (canManage) {
      const players = new Map();
      assignmentsArray.forEach(assignment => {
        if (assignment.playerId) {
          const playerId = assignment.playerId._id || assignment.playerId;
          const playerName = `${assignment.playerId.firstName || ''} ${assignment.playerId.lastName || ''}`.trim();
          if (!players.has(playerId) && playerName) {
            players.set(playerId, playerName);
          }
        }
      });
      setAvailablePlayers(Array.from(players, ([id, name]) => ({ id, name })));
    }
  };
  
  // Apply search and filters
  useEffect(() => {
    if (!allAssignments.active) return;
    
    let filtered = {
      active: [...allAssignments.active],
      overdue: [...allAssignments.overdue],
      history: [...allAssignments.history],
      stats: { ...allAssignments.stats }
    };
    
    // Apply search
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const searchFilter = (assignment) => {
        const equipment = assignment.equipmentId || {};
        const player = assignment.playerId || {};
        const playerName = `${player.firstName || ''} ${player.lastName || ''}`.toLowerCase();
        
        return (
          equipment.equipmentName?.toLowerCase().includes(search) ||
          playerName.includes(search) ||
          assignment.notes?.toLowerCase().includes(search)
        );
      };
      
      filtered.active = filtered.active.filter(searchFilter);
      filtered.overdue = filtered.overdue.filter(searchFilter);
      filtered.history = filtered.history.filter(searchFilter);
    }
    
    // Apply player filter (managers only)
    if (playerFilter && canManage) {
      const playerFilterFn = (assignment) => {
        const playerId = assignment.playerId?._id || assignment.playerId;
        return String(playerId) === String(playerFilter);
      };
      
      filtered.active = filtered.active.filter(playerFilterFn);
      filtered.overdue = filtered.overdue.filter(playerFilterFn);
      filtered.history = filtered.history.filter(playerFilterFn);
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      if (statusFilter === 'active') {
        filtered.overdue = [];
        filtered.history = [];
      } else if (statusFilter === 'overdue') {
        filtered.active = [];
        filtered.history = [];
      } else if (statusFilter === 'history') {
        filtered.active = [];
        filtered.overdue = [];
      }
    }
    
    setAssignments(filtered);
  }, [allAssignments, searchTerm, statusFilter, playerFilter, canManage]);
  
  const handleReturn = async (assignmentId) => {
    try {
      // Get Firebase auth token
      const currentUser = auth.currentUser;
      const token = currentUser ? await currentUser.getIdToken() : null;
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/equipment-assignments/${assignmentId}/mark-returned`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to mark as returned');
      }
      
      const data = await response.json();
      
      showSuccess('Equipment marked as returned!');
      
      // Refresh assignments
      fetchMyAssignments();
      
      // Show review prompt if applicable
      if (data.promptReview && data.canReview) {
        const assignment = assignments.active.find(a => a._id === assignmentId) || 
                         assignments.overdue.find(a => a._id === assignmentId);
        
        if (assignment && assignment.equipmentId) {
          setTimeout(() => {
            setEquipmentToReview(assignment.equipmentId);
            setShowReviewModal(true);
          }, 500);
        }
      }
    } catch (err) {
      console.error('Error returning equipment:', err);
      showError('Failed to mark equipment as returned');
    }
  };
  
  const handleReviewClick = (equipment) => {
    setEquipmentToReview(equipment);
    setShowReviewModal(true);
  };
  
  const handleEditClick = (assignment) => {
    setAssignmentToEdit(assignment);
    setShowEditModal(true);
  };
  
  const handleDeleteClick = (assignment) => {
    setAssignmentToDelete(assignment);
    setShowDeleteConfirm(true);
  };
  
  const confirmDelete = async () => {
    if (!assignmentToDelete) return;
    
    try {
      const currentUser = auth.currentUser;
      const token = currentUser ? await currentUser.getIdToken() : null;
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/equipment-assignments/${assignmentToDelete._id}`,
        {
          method: 'DELETE',
          headers: {
            ...(token && { 'Authorization': `Bearer ${token}` })
          }
        }
      );
      
      if (!response.ok) {
        throw new Error('Failed to delete assignment');
      }
      
      showSuccess('Assignment deleted successfully!');
      fetchMyAssignments(); // Refresh list
      
    } catch (error) {
      showError('Failed to delete assignment');
      console.error('Error deleting assignment:', error);
    } finally {
      setShowDeleteConfirm(false);
      setAssignmentToDelete(null);
    }
  };
  
  const handleEditComplete = () => {
    setShowEditModal(false);
    setAssignmentToEdit(null);
    fetchMyAssignments(); // Refresh list
  };
  
  const handleReviewSubmit = async (reviewData) => {
    try {
      // Get Firebase auth token
      const currentUser = auth.currentUser;
      const token = currentUser ? await currentUser.getIdToken() : null;
      
      // Ensure equipmentId is included
      const completeReviewData = {
        ...reviewData,
        equipmentId: equipmentToReview._id || equipmentToReview.id
      };
      
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/equipment-reviews`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token && { 'Authorization': `Bearer ${token}` })
          },
          body: JSON.stringify(completeReviewData)
        }
      );
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('Review submission failed:', errorData);
        throw new Error(errorData.error?.message || 'Failed to submit review');
      }
      
      showSuccess('Review submitted successfully!');
      fetchMyAssignments();
    } catch (err) {
      console.error('Error submitting review:', err);
      showError(err.message || 'Failed to submit review');
      throw err;
    }
  };
  
  const AssignmentCard = ({ assignment, type }) => {
    const equipment = assignment.equipmentId || {};
    const daysUntilDue = assignment.dueDate 
      ? Math.ceil((new Date(assignment.dueDate) - new Date()) / (1000 * 60 * 60 * 24))
      : null;
    
    return (
      <div className={`${styles.assignmentCard} ${styles[type]}`}>
        <div className={styles.cardImage}>
          <img 
            src={equipment.images?.[0]?.url || equipment.imageUrl || '/default-equipment.png'} 
            alt={equipment.equipmentName}
          />
          {type === 'overdue' && (
            <div className={styles.overdueBadge}>
              <FaExclamationTriangle /> Overdue
            </div>
          )}
          
          {/* Manager Actions - Hover Overlay (like equipment cards) */}
          {canManage && (
            <div className={styles.cardHoverActions}>
              <button 
                onClick={() => handleEditClick(assignment)}
                className={styles.editIconBtn}
                title="Edit Assignment"
              >
                <FaEdit />
              </button>
              <button 
                onClick={() => handleDeleteClick(assignment)}
                className={styles.deleteIconBtn}
                title="Delete Assignment"
              >
                <FaTrash />
              </button>
            </div>
          )}
        </div>
        
        <div className={styles.cardContent}>
          <h3 className={styles.equipmentName}>{equipment.equipmentName || 'Unknown Equipment'}</h3>
          
          {/* Show assigned player name for managers */}
          {canManage && assignment.playerId && (
            <div className={styles.playerName}>
              Assigned to: {assignment.playerId.firstName || ''} {assignment.playerId.lastName || ''}
            </div>
          )}
          
          <div className={styles.assignmentMeta}>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Assigned:</span>
              <span className={styles.metaValue}>
                {assignment.createdAt ? new Date(assignment.createdAt).toLocaleDateString() : 'N/A'}
              </span>
            </div>
            
            {assignment.dueDate && (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Due:</span>
                <span className={styles.metaValue}>
                  {new Date(assignment.dueDate).toLocaleDateString()}
                  {type === 'active' && daysUntilDue !== null && (
                    <span className={styles.daysRemaining}>
                      ({daysUntilDue} days {daysUntilDue < 0 ? 'overdue' : 'remaining'})
                    </span>
                  )}
                </span>
              </div>
            )}
            
            {assignment.returnedAt && (
              <div className={styles.metaItem}>
                <span className={styles.metaLabel}>Returned:</span>
                <span className={styles.metaValue}>
                  {new Date(assignment.returnedAt).toLocaleDateString()}
                </span>
              </div>
            )}
            
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Condition:</span>
              <span className={styles.metaValue}>{assignment.condition || 'Good'}</span>
            </div>
          </div>
          
          {assignment.notes && (
            <div className={styles.notes}>
              <FaClock /> {assignment.notes}
            </div>
          )}
        </div>
        
        <div className={styles.cardActions}>
          {(type === 'active' || type === 'overdue') && (
            <button 
              onClick={() => handleReturn(assignment._id)}
              className={styles.returnBtn}
            >
              <FaCheckCircle /> Mark as Returned
            </button>
          )}
          
          {type === 'history' && !assignment.hasBeenReviewed && equipment._id && (
            <button 
              onClick={() => handleReviewClick(equipment)}
              className={styles.reviewBtn}
            >
              <FaStar /> Write Review
            </button>
          )}
          
          {type === 'history' && assignment.hasBeenReviewed && (
            <div className={styles.reviewedBadge}>
              <FaCheckCircle /> Reviewed
            </div>
          )}
        </div>
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.loadingSpinner}></div>
        <p>Loading your assignments...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className={styles.error}>
        <p>Error: {error}</p>
      </div>
    );
  }
  
  const stats = assignments.stats || {
    currentlyActive: assignments.active?.length || 0,
    overdue: assignments.overdue?.length || 0,
    returned: assignments.history?.length || 0,
    reviewedCount: assignments.history?.filter(a => a.hasBeenReviewed).length || 0
  };
  
  const toReviewCount = assignments.history?.filter(a => !a.hasBeenReviewed).length || 0;
  
  return (
    <div className={styles.myAssignments}>
      {/* Search and Filters */}
      <div className={styles.controlsBar}>
        <div className={styles.searchBar}>
          <FaSearch className={styles.searchIcon} />
          <input
            type="text"
            placeholder="Search assignments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={styles.searchInput}
          />
        </div>
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className={styles.filterSelect}
        >
          <option value="all">All Status</option>
          <option value="active">Active Only</option>
          <option value="overdue">Overdue Only</option>
          <option value="history">History Only</option>
        </select>
        
        {/* Assigned To Filter (Managers only) */}
        {canManage && availablePlayers.length > 0 && (
          <select
            value={playerFilter}
            onChange={(e) => setPlayerFilter(e.target.value)}
            className={styles.filterSelect}
          >
            <option value="">All Players</option>
            {availablePlayers.map(player => (
              <option key={player.id} value={player.id}>
                {player.name}
              </option>
            ))}
          </select>
        )}
      </div>
      
      {/* Compact Stats - Inline */}
      <div className={styles.compactStats}>
        <span className={styles.statItem}>
          <FaClock style={{color: '#3B82F6'}} /> {stats.currentlyActive} Active
        </span>
        {stats.overdue > 0 && (
          <span className={styles.statItemWarning}>
            <FaExclamationTriangle /> {stats.overdue} Overdue
          </span>
        )}
        {toReviewCount > 0 && (
          <span className={styles.statItem}>
            <FaStar style={{color: '#FFD700'}} /> {toReviewCount} To Review
          </span>
        )}
        <span className={styles.statItem}>
          <FaCheckCircle style={{color: '#10b981'}} /> {stats.returned} Returned
        </span>
      </div>
      
      {/* Active Assignments */}
      {assignments.active && assignments.active.length > 0 && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>
            <FaClock /> Active Assignments ({assignments.active.length})
          </h2>
          <div className={styles.assignmentsGrid}>
            {assignments.active.map(assignment => (
              <AssignmentCard key={assignment._id} assignment={assignment} type="active" />
            ))}
          </div>
        </div>
      )}
      
      {/* Overdue Assignments */}
      {assignments.overdue && assignments.overdue.length > 0 && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>
            <FaExclamationTriangle /> Overdue Assignments ({assignments.overdue.length})
          </h2>
          <div className={styles.assignmentsGrid}>
            {assignments.overdue.map(assignment => (
              <AssignmentCard key={assignment._id} assignment={assignment} type="overdue" />
            ))}
          </div>
        </div>
      )}
      
      {/* History */}
      {assignments.history && assignments.history.length > 0 && (
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>
            <FaCheckCircle /> Assignment History ({assignments.history.length})
          </h2>
          <div className={styles.assignmentsGrid}>
            {assignments.history.map(assignment => (
              <AssignmentCard key={assignment._id} assignment={assignment} type="history" />
            ))}
          </div>
        </div>
      )}
      
      {/* Empty State */}
      {!assignments.active?.length && !assignments.overdue?.length && !assignments.history?.length && (
        <div className={styles.emptyState}>
          <FaClock className={styles.emptyIcon} />
          <p>No equipment assignments yet</p>
        </div>
      )}
      
      {/* Review Modal */}
      {showReviewModal && equipmentToReview && (
        <WriteReviewModal
          isOpen={showReviewModal}
          equipment={equipmentToReview}
          onClose={() => {
            setShowReviewModal(false);
            setEquipmentToReview(null);
          }}
          onSubmit={handleReviewSubmit}
        />
      )}
      
      {/* Edit Assignment Modal */}
      {showEditModal && assignmentToEdit && (
        <AssignEquipmentModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setAssignmentToEdit(null);
          }}
          programId={programId}
          assignmentToEdit={assignmentToEdit}
          onAssignmentUpdated={handleEditComplete}
        />
      )}
      
      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setAssignmentToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Assignment"
        message={assignmentToDelete 
          ? `Are you sure you want to delete the assignment for "${assignmentToDelete.equipmentId?.equipmentName || 'Unknown Equipment'}"? This action cannot be undone.`
          : ''
        }
      />
    </div>
  );
};

MyAssignmentsView.propTypes = {
  programId: PropTypes.string.isRequired,
  canManage: PropTypes.bool,
  userId: PropTypes.string
};

export default MyAssignmentsView;

